import flatten from 'lodash/flatten'
import maxBy from 'lodash/maxBy'
import uniqBy from 'lodash/uniqBy'
import {maybe, mapMaybes} from '@freckle/maybe'
import {groupAllWith, headOnNonEmpty} from '@freckle/non-empty'
import type {LangT} from '@freckle/educator-entities/ts/common/helpers/languages'
import {extractData, fancyClient} from '@freckle/educator-entities/ts/openapi-clients'
import type {CourseAttrs} from '@freckle/educator-entities/ts/roster/models/course'
import type {CourseMembershipAttrs} from '@freckle/educator-entities/ts/roster/models/course-membership'
import {getStudentIdsforCourse} from '@freckle/educator-entities/ts/users/logic/students'
import type {StudentAttrs} from '@freckle/educator-entities/ts/users/models/student'

export async function fetchStudents(): Promise<Array<StudentAttrs>> {
  return fancyClient
    .GET('/2/students')
    .then(extractData)
    .then(students =>
      students.map(s => ({
        ...s,
        grade: typeof s.grade === 'number' ? s.grade : parseInt(s.grade, 10),
        lang: s.lang as LangT,
        numberFactsLevelId: undefined,
        identityProvider:
          s.identityProvider === 'class-link-raw' ? 'class-link' : s.identityProvider,

        sisId: s.sisId,
        startingFreckleTextLevel: s.startingFreckleTextLevel,
        wordStudyDiagnosticLevel: s.wordStudyDiagnosticLevel,
        cleverStudentId: s.cleverStudentId,
        classLinkStudentId: s.classLinkStudentId,
        source: s.source,
        renaissanceRPIdentifier: s.renaissanceRPIdentifier,
        renaissanceManualRPIdentifier: s.renaissanceManualRPIdentifier,
        storeLimit: s.storeLimit
      }))
    )
}

export type StudentsMapT = Map<number, StudentAttrs>

export const indexStudentsById = (students: Array<StudentAttrs>): StudentsMapT =>
  new Map(students.map(student => [student.id, student]))

// FIXME: The student collection is not shared so we have to duplicate this function
// We need to find a way to share the student Collection
function forCourse(
  studentCollection: StudentsMapT,
  courseId: number,
  courseMemberships: Array<CourseMembershipAttrs>
): Array<StudentAttrs> {
  const studIdsInCourse = getStudentIdsforCourse(courseId, courseMemberships)
  return mapMaybes(studIdsInCourse, studId => studentCollection.get(studId))
}

export function forTeacher(
  studentCollection: StudentsMapT,
  teacherId: number,
  courses: Array<CourseAttrs>,
  courseMemberships: Array<CourseMembershipAttrs>
): Array<StudentAttrs> {
  const teacherCourses = courses.filter(course => course.teacherId === teacherId)
  return uniqBy(
    flatten(
      teacherCourses.map(course => forCourse(studentCollection, course.id, courseMemberships))
    ),
    student => student.id
  )
}

export function getGradeWithMostStudents(
  studentCollection: Array<StudentAttrs>,
  domainGradeCoverage: Set<number>
): number | null {
  const studentsInGradeCoverage = studentCollection.filter(s => domainGradeCoverage.has(s.grade))
  const studentsGroupedByGrade = groupAllWith(({grade}) => grade, studentsInGradeCoverage)
  const groupingWithMostStudents = maxBy(studentsGroupedByGrade, group => group.length)
  return maybe(
    () => null,
    group => headOnNonEmpty(group).grade,
    groupingWithMostStudents
  )
}
